import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Map } from 'immutable';
import { createFavorite, deleteFavorite } from 'flow/actions/favorites-actions';
import ContactSettingRow from 'flow/components/contact-card/contact-fields/ContactSettingRow';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import selectFavoriteData, {
    selectFavoriteFromEntityKey,
} from 'flow/selectors/favorite-selectors';
import { Contact, ContactFull } from 'flow/types/Contact';
import { FavoriteEntityKey } from 'flow/types/Favorite';
import { TypedMap } from 'flow/types/TypedMap';
import { isContactKey, isExtensionKey, isQueueKey } from 'flow/utils';
import { FavoriteType } from 'flow/utils/favorite-utils';

interface Props {
    favoriteTypeKey: FavoriteEntityKey;
    /**
     * The favourite's data when the store has none for the key yet: a
     * ticket's contact comes from the ticket's visitor details before (or
     * without) `fetchContact` putting it in the contacts store.
     */
    fallbackData?: TypedMap<Contact> | TypedMap<ContactFull>;
    className?: string;
}

const getFavoriteField = (favoriteTypeKey: FavoriteEntityKey) => {
    if (isExtensionKey(favoriteTypeKey)) {
        return 'extension';
    }
    if (isContactKey(favoriteTypeKey)) {
        return 'contact';
    }
    if (isQueueKey(favoriteTypeKey)) {
        return 'queue';
    }
    return null;
};

const FavoriteListItem = ({
    favoriteTypeKey,
    fallbackData,
    className,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const favorites = useAppSelector((state) => state.favorites);
    const favoriteData = useAppSelector((state) =>
        selectFavoriteData(favoriteTypeKey, state),
    );
    const favorite = selectFavoriteFromEntityKey(favoriteTypeKey, favorites);
    const title = i18next.t('General.Favorite.Toggle.Title');

    const handleFavorite = (checked: boolean) => {
        if (!checked) {
            if (favorite) {
                dispatch(deleteFavorite(favorite.get('key')));
            }
            return;
        }

        const field = getFavoriteField(favoriteTypeKey);
        if (!field) {
            return;
        }

        dispatch(
            createFavorite(
                Map({
                    type: FavoriteType[field],
                    [field]: favoriteData ?? fallbackData,
                }),
            ),
        );
        setTimeout(() => {
            history.push(pathname);
        }, 200);
    };

    return (
        <ContactSettingRow
            title={title}
            hint={i18next.t('ContactCard.Favorite.Hint')}
            className={className}
        >
            <Toggle onChange={handleFavorite} value={!!favorite} text={title} />
        </ContactSettingRow>
    );
};

export default FavoriteListItem;
